import { z } from 'zod';
import { toBqAudienceSegment } from '../constants/audience-segment.js';
import { isAcceptableDateWindow, isValidYmd, MAX_DAYS_BACK } from '../filters/date-window.js';

// ─── Advanced filter types (used by the drilldown "+ Filter" feature) ─────────

export const FilterConnectorSchema = z.enum(['And', 'Or']);
export type FilterConnector = z.infer<typeof FilterConnectorSchema>;

export const FilterOperatorSchema = z.enum([
  // String operators
  'includes', 'not_includes', 'is', 'is_not', 'is_empty', 'is_not_empty',
  // Numeric operators
  'lt', 'gt', 'eq', 'neq', 'gte', 'lte',
]);
export type FilterOperator = z.infer<typeof FilterOperatorSchema>;

export const FilterRuleSchema = z.object({
  connector: FilterConnectorSchema,
  field: z.string(),
  operator: FilterOperatorSchema,
  value: z.string(),
});
export type FilterRule = z.infer<typeof FilterRuleSchema>;

export const FilterGroupSchema = z.object({
  connector: FilterConnectorSchema,
  rules: z.array(FilterRuleSchema).min(1),
});
export type FilterGroup = z.infer<typeof FilterGroupSchema>;

export const AdvancedFiltersSchema = z.array(FilterGroupSchema);
export type AdvancedFilters = z.infer<typeof AdvancedFiltersSchema>;

export const FatigueGranularitySchema = z.enum(['daily', 'weekly', 'monthly']);
export type FatigueGranularity = z.infer<typeof FatigueGranularitySchema>;

export const WorkspacePage = z.enum([
  'dashboard',
  'overview',
  'drilldown',
  'drilldown-my-brand-drawer',
  'drilldown-my-brand-drawer-performance',
  'drilldown-my-brand-drawer-fatigue',
  'overview-topic-detail-examples',
  'overview-topic-detail-fatigue',
  'overview-comp-topic-detail-examples',
  'overview-comp-topic-detail-fatigue',
]);
export type WorkspacePage = z.infer<typeof WorkspacePage>;

export const DashboardMode = z.enum(['competitors', 'my-brand']);
export type DashboardMode = z.infer<typeof DashboardMode>;

/**
 * The selectable date ranges, as plain data. Exported so consumers that cannot take a
 * zod 3 schema — `packages/mcp` is on zod 4 — can still advertise the same values
 * instead of restating them and drifting.
 */
export const WORKSPACE_DATE_RANGES = ['7d', '30d', '90d', '12m'] as const;

const DateRange = z.enum(WORKSPACE_DATE_RANGES);

export const CompetitorFiltersSchema = z.object({
  myIndustry: z.string().nullable(),
  myBrand: z.string().nullable(),
  industryProducts: z.array(z.string()),
  competitorsIndustry: z.string().nullable(),
  mainCompetitor: z.string().nullable(),
  benchmark: z.array(z.string()),
  // Ad platforms (BQ `rt.source`) to include. Empty = all platforms.
  // The API HTTP layer defaults this to ['meta'] for Retool parity.
  platforms: z.array(z.string()),
  dateRange: DateRange,
});

export const MyBrandFiltersSchema = z.object({
  platforms: z.array(z.string()),
  accounts: z.array(z.string()),
  products: z.array(z.string()),
  dateRange: DateRange,
});

/** One row from the My Brand User_Data BQ query — drives filter cascading on the FE. */
export const MyBrandFilterTupleSchema = z.object({
  platform: z.string(),
  accountName: z.string(),
  productCode: z.string(),
});

export const MyBrandFilterOptionsResponseSchema = z.object({
  tuples: z.array(MyBrandFilterTupleSchema),
});

export type MyBrandFilterTuple = z.infer<typeof MyBrandFilterTupleSchema>;
export type MyBrandFilterOptionsResponse = z.infer<typeof MyBrandFilterOptionsResponseSchema>;

export type MyBrandFilters = z.infer<typeof MyBrandFiltersSchema>;

export const WorkspaceFiltersSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('competitors'), filters: CompetitorFiltersSchema }),
  z.object({ mode: z.literal('my-brand'), filters: MyBrandFiltersSchema }),
]);

export type WorkspaceFilters = z.infer<typeof WorkspaceFiltersSchema>;

/**
 * Page-scoped filters for the Drilldown ("Mix Chart") page. These sit alongside
 * the global mode-discriminated `filtersPayload`; the global filters drive
 * industry/competitor/product/source/days_back, while these drive the
 * segment grouping and the object-type / effective-status / date-window slice.
 *
 * Three NAMED segment fields (not an array): defaults differ per position and
 * the index↔number mapping is fragile. `segment1='Hash'` is the default active
 * grouping (rendered as "Creative" in the UI); 'All' positions collapse to the
 * SQL's ELSE branch (`Groupby2/3 = 'All'`).
 */
/**
 * Sort field ids for the Drilldown "Order by" control. Competitors mode sorts by
 * these fixed aggregate labels; My Brand mode sorts by any KPI column returned by
 * `DrilldownMyBrandRow` (services/workspace/src/bq-queries/drilldown-my-brand.sql.ts) —
 * the ids here are the SINGLE SOURCE OF TRUTH for that row's sortable column names,
 * consumed by the SQL builder's generated ORDER BY CASE (never hand-duplicated there)
 * and by the frontend's My Brand KPI catalog (frontend/web/src/features/drilldown/lib/
 * my-brand-kpi-catalog.ts), so the store id → URL → contract enum → SQL branch chain
 * can't silently drift when a field is added or renamed.
 */
export const COMPETITOR_SORT_FIELDS = ['Days', 'Assets', 'Days/Assets', 'Date'] as const;

export const MY_BRAND_SORT_FIELDS = [
  'spend', 'cpa', 'roas', 'conversions', 'value', 'aov', 'creatives', 'ads',
  'cpc', 'cpm', 'ctr', 'cr', 'thumbstop', 'holdrate', 'frequency', 'engagement',
  'cost_per_like', 'cost_per_custom', 'cost_per_comment', 'cost_per_post_eng', 'spend_per_creative',
  'impressions', 'link_click', 'clicks', 'video_play_action', 'video_thruplay_actions',
  'avg_watch_time', 'likes', 'comments', 'post_engagements', 'custom_event',
] as const;

export const DRILLDOWN_SORT_VALUES = [...COMPETITOR_SORT_FIELDS, ...MY_BRAND_SORT_FIELDS] as const;

// Pure transport shape — defaults live at the edges (the API route query schema
// and the FE store), not here, so input and output types stay identical.
export const DrilldownPageFiltersSchema = z.object({
  objectType: z.array(z.string()),
  effectiveStatus: z.array(z.string()),
  // 'YYYY-MM-DD' or '' (empty = open bound, matching the Retool query sentinel).
  startDate: z.string(),
  endDate: z.string(),
  // Group-by segment values: segment1 defaults to 'Hash' (shown as "Creative"),
  // segment2/segment3 to 'All'. Defaults are applied by the route/store.
  segment1: z.string(),
  segment2: z.string(),
  segment3: z.string(),
  // Row offset for infinite-scroll pagination (0-based). Part of the page
  // filters so it flows into the cache/idempotency hash — each page caches
  // independently. Required (no default) to keep the event payload's input and
  // output types aligned; the API route always supplies it.
  offset: z.number().int().min(0),
  // Sort controls — surfaced in the UI as "Order by" + "Asc/Desc" toggle (competitors
  // mode) or the KPI pill row's sort arrows (My Brand mode). See DRILLDOWN_SORT_VALUES.
  // Competitor values stay in COMPETITOR_SORT_FIELDS. My Brand values are the
  // tenant's resolved `Kpi.name`s (plus a one-time legacy snake_case map). The
  // closed DRILLDOWN_SORT_VALUES enum blocked custom KPI sort; the API checks
  // the resolved catalog (or the competitor enum) after this parse.
  sortMode: z.string().min(1).default('Days/Assets'),
  sortDirection: z.enum(['asc', 'desc']).default('desc'),
  // Advanced filter rules/groups wired to the "+ Filter" toolbar button.
  advancedFilters: AdvancedFiltersSchema.optional().default([]),
  // Audience segment (`core.raw_table_view`.`audience:audience_segment`).
  // Empty = All (BQ short-circuits via `ARRAY_LENGTH(@audienceSegment) = 0`).
  // Parsed output is the canonical BQ list (`toBqAudienceSegment`).
  audienceSegment: z
    .array(z.string())
    .default([])
    .transform(toBqAudienceSegment),
});

export type DrilldownPageFilters = z.infer<typeof DrilldownPageFiltersSchema>;

// ─── KPI card types (presentation-free — no CSS variables here) ──────────────

export interface KpiTimeSeriesPoint {
  dayOffset: number;  // 0 = first day of the period — used as zip key
  date:      string;  // 'YYYY-MM-DD'
  value:     number;
}

export interface KpiTimeSeries {
  current:  KpiTimeSeriesPoint[];
  previous: KpiTimeSeriesPoint[];
}

export const NorthStarKpiSchema = z.enum(['roas', 'cpa', 'custom']);
export type NorthStarKpi = z.infer<typeof NorthStarKpiSchema>;

export type MyBrandKpiMetric =
  | 'spend'
  | 'northStarRoas'
  | 'northStarCpa'
  | 'northStarCustom'
  | 'creativeVolume'
  | 'creativeConcentration'
  | 'stickRate'
  | 'winners';

export interface MyBrandKpiCard {
  metric:        MyBrandKpiMetric;
  label:         string;
  value:         number;
  previousValue: number;
  format:        'currency' | 'count' | 'percent' | 'ratio';
  timeSeries?:   KpiTimeSeries;
}

export interface MyBrandNorthStarGroup {
  roas:   MyBrandKpiCard;
  cpa:    MyBrandKpiCard;
  custom: MyBrandKpiCard;
}

export interface MyBrandKpiCards {
  spend:                 MyBrandKpiCard;
  northStar:             MyBrandNorthStarGroup;
  creativeVolume:        MyBrandKpiCard;
  creativeConcentration: MyBrandKpiCard;
  stickRate:             MyBrandNorthStarGroup;
  winners:               MyBrandNorthStarGroup;
}

/** Standalone per-metric time series for the My Brand chart, loaded separately
 *  from the KPI cards so the cards can render before the chart data arrives. */
export interface MyBrandKpiCharts {
  spend:                 KpiTimeSeries;
  northStarRoas:         KpiTimeSeries;
  northStarCpa:          KpiTimeSeries;
  northStarCustom:       KpiTimeSeries;
  creativeVolume:        KpiTimeSeries;
  creativeConcentration: KpiTimeSeries;
  stickRate:             KpiTimeSeries;
  winnersRoas:           KpiTimeSeries;
  winnersCpa:            KpiTimeSeries;
  winnersCustom:         KpiTimeSeries;
}

export type CompetitorFilters = z.infer<typeof CompetitorFiltersSchema>;

export type CompetitorKpiMetric =
  | 'activeAds'
  | 'adsLaunched'
  | 'netNewCreatives'
  | 'newHooks'
  | 'creativeLifespan'
  | 'landingPages';

export interface CompetitorKpiCard {
  metric: CompetitorKpiMetric;
  label: string;
  format: 'count';
  you: number;
  mainCompetitor: number;
  benchmark: number;
  brandLabels: { you: string; mainCompetitor: string };
}

export interface CompetitorKpiCards {
  activeAds: CompetitorKpiCard;
  adsLaunched: CompetitorKpiCard;
  netNewCreatives: CompetitorKpiCard;
  newHooks: CompetitorKpiCard;
  creativeLifespan: CompetitorKpiCard;
  landingPages: CompetitorKpiCard;
}

export const CompetitorKpiCardSchema = z.object({
  metric: z.enum([
    'activeAds',
    'adsLaunched',
    'netNewCreatives',
    'newHooks',
    'creativeLifespan',
    'landingPages',
  ]),
  label: z.string(),
  format: z.literal('count'),
  you: z.number(),
  mainCompetitor: z.number(),
  benchmark: z.number(),
  brandLabels: z.object({
    you: z.string(),
    mainCompetitor: z.string(),
  }),
});

export const CompetitorKpiCardsSchema = z.object({
  activeAds: CompetitorKpiCardSchema,
  adsLaunched: CompetitorKpiCardSchema,
  netNewCreatives: CompetitorKpiCardSchema,
  newHooks: CompetitorKpiCardSchema,
  creativeLifespan: CompetitorKpiCardSchema,
  landingPages: CompetitorKpiCardSchema,
});

export interface CompetitorKpiChartPoint {
  date: string;
  you: number;
  mainCompetitor: number;
  benchmark: number;
}

export const CompetitorKpiChartPointSchema = z.object({
  date: z.string(),
  you: z.number(),
  mainCompetitor: z.number(),
  benchmark: z.number(),
});

export interface CompetitorKpiCharts {
  bucket: 'week' | 'month';
  brandLabels: { you: string; mainCompetitor: string };
  activeAds: CompetitorKpiChartPoint[];
  adsLaunched: CompetitorKpiChartPoint[];
  netNewCreatives: CompetitorKpiChartPoint[];
  newHooks: CompetitorKpiChartPoint[];
  creativeLifespan: CompetitorKpiChartPoint[];
  landingPages: CompetitorKpiChartPoint[];
}

const competitorKpiChartSeriesSchema = z.array(CompetitorKpiChartPointSchema);

export const CompetitorKpiChartsSchema = z.object({
  bucket: z.enum(['week', 'month']),
  brandLabels: z.object({
    you: z.string(),
    mainCompetitor: z.string(),
  }),
  activeAds: competitorKpiChartSeriesSchema,
  adsLaunched: competitorKpiChartSeriesSchema,
  netNewCreatives: competitorKpiChartSeriesSchema,
  newHooks: competitorKpiChartSeriesSchema,
  creativeLifespan: competitorKpiChartSeriesSchema,
  landingPages: competitorKpiChartSeriesSchema,
});

export const MyBrandKpiCardSchema = z.object({
  metric: z.string(),
  label: z.string(),
  value: z.number(),
  previousValue: z.number(),
  format: z.enum(['currency', 'count', 'percent', 'ratio']),
  timeSeries: z
    .object({
      current: z.array(
        z.object({
          dayOffset: z.number(),
          date: z.string(),
          value: z.number(),
        }),
      ),
      previous: z.array(
        z.object({
          dayOffset: z.number(),
          date: z.string(),
          value: z.number(),
        }),
      ),
    })
    .optional(),
});

export const MyBrandNorthStarGroupSchema = z.object({
  roas: MyBrandKpiCardSchema,
  cpa: MyBrandKpiCardSchema,
  custom: MyBrandKpiCardSchema,
});

export const MyBrandKpiCardsSchema = z.object({
  spend: MyBrandKpiCardSchema,
  northStar: MyBrandNorthStarGroupSchema,
  creativeVolume: MyBrandKpiCardSchema,
  creativeConcentration: MyBrandKpiCardSchema,
  stickRate: MyBrandNorthStarGroupSchema,
  winners: MyBrandNorthStarGroupSchema,
});

export const DashboardKpiPrecacheStatusEnum = z.enum(['ready', 'failed', 'skipped']);
export type DashboardKpiPrecacheStatus = z.infer<typeof DashboardKpiPrecacheStatusEnum>;

// ─── Overview report row (BQ SELECT result) ──────────────────────────────────

export interface OverviewReportRow {
  Unique_id: number;
  combination: string;
  product_code: string;
  grouping_key: string;
  feature_time_frame: string;
  object_type: string;
  Topic_Title: string;
  Topic_Description: string;
  creatives: number;
  ads: number;
  sticked: number;
  value: number;
  conversions: number;
  SPEND: number;
  avg_watch_time: number;
  clicks: number;
  custom_event: number;
  impressions: number;
  link_click: number;
  video_play_action: number;
  video_thruplay_actions: number;
  frequency: number;
  likes: number;
  comments: number;
  post_engagements: number;
  Scenario: string;
  Sub_Feature: string;
  RowNum: number;
  adge_url: string;
  examples: string;
  examples_spend: number;
  // ─── De-duplicated cross-topic grand totals (identical on every row) ──────────
  // Each creative (hash/ad_id) counted ONCE across the filtered topic universe, so
  // these are NOT the Σ-over-topics of the per-row columns (which double-count a
  // creative that matches several topics). They are the baseline for the Topic Detail
  // drawer's vs-average % badges and share denominators. Optional: only the My-Brand
  // overview query emits them (absent on hand-built rows / other producers).
  total_value?: number;
  total_conversions?: number;
  total_spend?: number;
  total_clicks?: number;
  total_custom_event?: number;
  total_impressions?: number;
  total_link_click?: number;
  total_video_play_action?: number;
  total_video_thruplay_actions?: number;
  total_likes?: number;
  total_comments?: number;
  total_post_engagements?: number;
  total_creatives?: number;
  total_ads?: number;
  total_sticked?: number;
  total_frequency?: number;
}

// ─── Competitors overview report row (different BQ — Days/Assets, not SPEND) ──
// Shape of the "Competitor With Variables" query's final SELECT. The competitor
// state runs a distinct query over trump.competitor_raw_table.
export interface OverviewCompetitorReportRow {
  Unique_ID: number;
  combination: string;
  grouping_key: string;
  feature_time_frame: string;
  object_type: string;
  Topic_Title: string;
  Topic_Description: string;
  Days: number;
  Assets: number;
  Scenario: string;
  Sub_Feature: string;
  RowNum: number;
  adge_url: string;
  Examples: string;
  Examples_Days: number;
  Examples_Assets: number;
}

// ─── Competitors comparison chart row (per topic, per entity bucket) ──────────
// Feeds the Competitive Comparison chart. The handler buckets BQ competitors into
// you / mainCompetitor / benchmark and applies the per-dataset Retool transform
// (time-frame selection, per-dataset Asset %, winsorized-baseline normalization).
// Everything metric-agnostic is precomputed here; the frontend picks which value to
// plot per the (frontend-only) Performance Metric pill:
//   Days       → Days (raw)
//   Assets     → Assets (raw)
//   Days/Assets→ daysAssets (winsorized-normalized, clamped to [-1, 1])
export interface OverviewCompetitorChartRow {
  Topic_Title: string;
  bucket: 'you' | 'mainCompetitor' | 'benchmark';
  Days: number;
  Assets: number;
  /**
   * Days/Assets normalized against the bucket's winsorized baseline and clamped to
   * [-1, 1]. `null` when no baseline exists (empty/zero baseline or no assets) →
   * the frontend renders NO dot (distinct from a real 0).
   */
  daysAssets: number | null;
  /**
   * Topic's share of its bucket's total assets (0..1), summed over ALL topics in the
   * bucket (matches Retool — no top-N slice). `0` when the bucket has no assets →
   * renders a 0-height bar (distinct from `daysAssets`'s null sentinel).
   */
  assetPct: number;
}

// ─── My-Brand overview chart row (per-Topic_Title aggregation) ────────────────
// Server-side rollup of the My-Brand BQ rows (OverviewReportRow) by Topic_Title (the
// concept/topic — the chart's x-axis category, filtered by Scenario): raw columns are
// SUMMED across the topic's rows; `frequency` is a ratio, so it is neither summed nor
// plain-averaged — it is an IMPRESSION-WEIGHTED mean (Σ(freqᵢ·impᵢ)/Σ impᵢ, and 0 when
// total impressions = 0). The frontend derives every selectable KPI from these
// aggregates (see metricCatalog.ts). Ratio metrics divide summed numerator/denominator —
// never average per-row ratios. Field names are the catalog's own lowercased keys (the
// raw OverviewReportRow columns use mixed casing, e.g. `SPEND` → `spend`) so the compute
// fns read them directly. See aggregateOverviewChartRows for the exact rollup logic.
export interface OverviewChartRow {
  /** X-axis category — the raw Topic_Title; formatted/truncated client-side for display. */
  Topic_Title: string;
  spend: number;
  sticked: number;
  value: number;
  conversions: number;
  clicks: number;
  impressions: number;
  link_click: number;
  video_play_action: number;
  video_thruplay_actions: number;
  likes: number;
  custom_event: number;
  comments: number;
  post_engagements: number;
  creatives: number;
  ads: number;
  /** Impression-weighted mean frequency across the topic's rows (0 when Σ impressions = 0). */
  frequency: number;
}

// ─── My-Brand per-KPI Overview series (DB-formula-driven) ─────────────────────
// One entry PER VISIBLE KPI (from the `kpis` table), each carrying that KPI's
// per-topic value + a server-formatted string + a per-value heatmap color + the
// post-sort rank. Ports Retool's per-KPI Overview transformer; KPI math comes from
// `Kpi.formula` (not the hardcoded frontend metric catalog). Additive — emitted
// alongside `myBrandChartRows`, which is unchanged.

/** One topic's value for a single KPI. */
export interface OverviewKpiPoint {
  /** X-axis category — the raw Topic_Title (matches OverviewChartRow). */
  Topic_Title: string;
  /** Formula result for this topic. `null` ⇒ divide-by-zero / no data ⇒ no bar. */
  value: number | null;
  /** Display string for `value` (e.g. "$765,452", "1.15%", "—" when null). */
  formatted: string;
  /** Per-value heatmap color over this KPI's own min/max (honors the KPI's sort dir). */
  scatter_color: string;
  /** 0-based rank AFTER sorting by the KPI's `polarity` (nulls last). Top = 0. */
  index: number;
}

/** A single visible KPI's per-topic series plus its series-level average. */
export interface OverviewKpiSeries {
  /** Canonical KPI id (`Kpi.name`, e.g. "Spend", "CPA"). */
  kpi: string;
  /** Tenant-facing label (`Kpi.displayName`). Falls back to `kpi` when omitted. */
  displayName?: string;
  /** From `Kpi.polarity`: POSITIVE means a value above zero is good. */
  polarity?: 'POSITIVE' | 'NEGATIVE';
  /** Global average: formula evaluated against cross-topic summed totals. `null` on div-by-zero. */
  avg: number | null;
  /** Server-formatted `avg` (same KPI format) — used for the "value/total" share display. */
  avgFormatted?: string;
  /**
   * KPI display format (from `Kpi.format`). Lets the drawer round a topic value to its
   * DISPLAYED precision before computing the vs-average % — Retool derives the badge from
   * the shown value (e.g. ROAS 0.95, not 0.9541), so percent values round on their ×100 form.
   */
  format?: 'CURRENCY' | 'PERCENT' | 'NUMBER';
  /**
   * True when this KPI's `Kpi.formula` contains a `SUM(` token. Retool suppresses the
   * vs-average badge for exactly these KPIs — `isShareOfTotalKPI` in its overview transformer
   * tests `/SUM\s*\(/i` against the formula and renders a bare formatted value with no
   * parenthetical delta. Sent from the server because the frontend has no access to the formula
   * string; without it the drawer has to hardcode a KPI-name list that silently rots whenever a
   * tenant adds a `SUM(`-based formula.
   */
  hasSumFormula?: boolean;
  data: OverviewKpiPoint[];
}

/**
 * Per-(Topic_Title, week_start, bucket) weekly asset count for the "Overtime View"
 * chart. `week_start` is the Sunday-anchored ISO week date (YYYY-MM-DD).
 * Aggregated across all feature_time_frame values — the time-frame breakdown is
 * intentionally collapsed at this grain.
 */
export interface OverviewTopicWeeklyRow {
  Topic_Title: string;
  /** ISO date string (YYYY-MM-DD) — Sunday anchor of the week. */
  week_start: string;
  bucket: 'you' | 'mainCompetitor' | 'benchmark';
  Assets: number;
}

// ─── Topic detail drawer (Core My Brand) ──────────────────────────────────────

/** Topic-scoped reco/segment params carried on Examples and Fatigue endpoints. */
export const TopicDetailContextQuerySchema = z.object({
  search: z.string().default('All'),
  segment1: z.string().default('Hash'),
  segment2: z.string().default('All'),
  segment3: z.string().default('All'),
  topicTitle: z.string().optional(),
});

export type TopicDetailContextQuery = z.infer<typeof TopicDetailContextQuerySchema>;

/** One creative row in the Topic Detail Examples tab. */
export interface TopicExampleRow {
  hash: string;
  thumbnailUrl: string;
  creativeUrl: string;
  /** Segment3 value — displayed as Ad Name in the Examples table. */
  adName: string;
  /** Meta Ad Library URL for the ad, or "" when no single ad id is available. */
  adLibUrl: string;
  /** Segment2 value — the topic example label. */
  example: string;
  datesActive: string;
  spend: number;
  cpa: number | null;
  roas: number | null;
  ads: number;
  objectType: string;
  scatterColor: string;
  /** Visible KPI values keyed by KPI display name. */
  metrics: Record<string, number | null>;
  /** Formatted strings for share KPIs (SUM() formulas). */
  formattedMetrics: Record<string, string>;
}

/** One time-bucket point for the Topic Detail Fatigue line chart. */
export interface TopicFatiguePoint {
  date: string;
  value: number;
  /** Raw group-by value (e.g. facebook). Omitted when the chart is a single aggregated series. */
  series?: string;
}

// ─── Topic detail drawer (Competitors mode) ───────────────────────────────────

export const CompTopicDetailBucket = z.enum(['you', 'mainCompetitor', 'benchmark']);
export type CompTopicDetailBucket = z.infer<typeof CompTopicDetailBucket>;

/** Topic + bucket scoped params for Comp Examples and Fatigue endpoints. */
export const CompTopicDetailContextQuerySchema = z.object({
  search: z.string().default('All'),
  segment1: z.string().default('Hash'),
  segment2: z.string().default('All'),
  segment3: z.string().default('All'),
  topicTitle: z.string().optional(),
  competitorBqFilter: z.string().min(1),
  bucket: CompTopicDetailBucket,
});

export type CompTopicDetailContextQuery = z.infer<typeof CompTopicDetailContextQuerySchema>;

/** One creative row in the Comp Topic Detail Examples tab. */
export interface CompTopicExampleRow {
  hash: string;
  thumbnailUrl: string;
  creativeUrl: string;
  adName: string;
  /** Meta Ad Library URL for the ad, or "" when no single ad id is available. */
  adLibUrl: string;
  days: number;
  assets: number;
  daysPerAsset: number | null;
  adgeScore: number | null;
  ads: number;
  competitor: string;
  objectType: string;
}

/** One weekly bucket for the Comp Topic Detail Fatigue line chart. */
export interface CompTopicFatiguePoint {
  periodStart: string;
  days: number;
  assets: number;
  daysPerAsset: number | null;
  assetPct: number | null;
}

// ─── Overview filters (BQ-bound only — breakdown is frontend-only, excluded here) ──

export const OverviewFiltersSchema = z.object({
  groupBy: z.string().default(''),
  subFeature: z.array(z.string()).default([]),
  featureTimeFrame: z.array(z.string()).default([]),
  objectType: z.array(z.string()).default([]),
  effectiveStatus: z.array(z.string()).default([]),
  // Empty array = All (BQ short-circuits via `ARRAY_LENGTH(@scenario) = 0`).
  scenario: z.array(z.string()).default([]),
  // Values of the selected `groupBy` dimension to filter on (Age → ['18-24', …]).
  // Empty = All (BQ short-circuits via `ARRAY_LENGTH(@groupByValues) = 0`). The value
  // list is dimension-specific and reset whenever `groupBy` changes on the frontend.
  groupByValues: z.array(z.string()).default([]),
  // Audience segment (`core.raw_table_view`.`audience:audience_segment`).
  // Empty = All (BQ short-circuits via `ARRAY_LENGTH(@audienceSegment) = 0`).
  // Parsed output is the canonical BQ list (`toBqAudienceSegment`).
  audienceSegment: z
    .array(z.string())
    .default([])
    .transform(toBqAudienceSegment),
  // Closed window the BFF resolves before publish. Defaulted empty so a stale
  // Pub/Sub payload from an older API pod still parses; the builder rejects
  // the empty bound rather than scanning unbounded.
  startDate: z.string().default(''),
  endDate: z.string().default(''),
  // Advanced filter rules/groups wired to the "+ Filter" toolbar button. Empty = no
  // HAVING clause (query identical to pre-feature). Values are bound as typed BQ params.
  advancedFilters: AdvancedFiltersSchema.optional().default([]),
});

export type OverviewFilters = z.infer<typeof OverviewFiltersSchema>;

// ─── Advanced-filter field catalog (single source of truth) ──────────────────
// Shared by the Overview filter-bar options (frontend) and the BQ HAVING
// field→expression maps (backend). Keeping the key set in one place stops a typo
// from silently drifting to a `TRUE` no-op (an unknown field key never errors —
// it just fails to filter). `type` drives both the UI operator set and the BQ
// param binding (STRING vs FLOAT64).

export interface OverviewAdvancedFilterField {
  /** Stable key — must match the backend HAVING field→expression map for the mode. */
  key: string;
  /** UI label shown in the field selector. */
  label: string;
  type: 'string' | 'numeric';
}

export const OVERVIEW_ADVANCED_FILTER_FIELDS: Record<DashboardMode, OverviewAdvancedFilterField[]> = {
  competitors: [
    { key: 'Days', label: 'Days', type: 'numeric' },
    { key: 'Assets', label: 'Assets', type: 'numeric' },
    { key: 'DaysPerAsset', label: 'Days/Assets', type: 'numeric' },
    { key: 'Topic_Title', label: 'Topic', type: 'string' },
    { key: 'Scenario', label: 'Scenario', type: 'string' },
    { key: 'Sub_Feature', label: 'Sub-categories', type: 'string' },
    { key: 'feature_time_frame', label: 'Section', type: 'string' },
  ],
  'my-brand': [
    { key: 'SPEND', label: 'Spend', type: 'numeric' },
    { key: 'creatives', label: 'Creatives', type: 'numeric' },
    { key: 'ads', label: 'Ads', type: 'numeric' },
    { key: 'conversions', label: 'Conversions', type: 'numeric' },
    { key: 'clicks', label: 'Clicks', type: 'numeric' },
    { key: 'impressions', label: 'Impressions', type: 'numeric' },
  ],
};

// ─── Event payload ────────────────────────────────────────────────────────────

/** For dashboard my-brand, selects which dataset the worker computes so cards
 *  and chart load as independent jobs. Absent = legacy behavior (both / competitors). */
export const WorkspaceDataSection = z.enum(['cards', 'chart']);
export type WorkspaceDataSection = z.infer<typeof WorkspaceDataSection>;

const dashboardYmd = (field: 'startDate' | 'endDate') =>
  z.string().refine(isValidYmd, {
    message: `Invalid date for ${field} (expected YYYY-MM-DD)`,
  });

/** Page-scoped Dashboard window. Optional so token-only in-flight events still parse. */
export const DashboardDateWindowSchema = z.object({
  startDate: dashboardYmd('startDate'),
  endDate: dashboardYmd('endDate'),
});
export type DashboardDateWindow = z.infer<typeof DashboardDateWindowSchema>;

export const WorkspaceDataRefreshRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  page: WorkspacePage,
  filtersHash: z.string(),
  filtersPayload: WorkspaceFiltersSchema,
  overviewFilters: OverviewFiltersSchema.optional(),
  dashboardDateWindow: DashboardDateWindowSchema.optional(),
  section: WorkspaceDataSection.optional(),
  // Present for all drilldown pages: 'drilldown', 'drilldown-my-brand-drawer',
  // 'drilldown-my-brand-drawer-performance', and 'drilldown-my-brand-drawer-fatigue'.
  drilldownFilters: DrilldownPageFiltersSchema.optional(),
  // Creative hash for drawer pages — required when page === 'drilldown-my-brand-drawer',
  // 'drilldown-my-brand-drawer-performance', or 'drilldown-my-brand-drawer-fatigue'.
  hash: z.string().optional(),
  // Optional ad_id scope for Performance drawer (omit for hash aggregate).
  adId: z.string().optional(),
  // Fatigue tab: selected KPI ids + bucket granularity.
  fatigueMetrics: z.array(z.string()).optional(),
  fatigueGranularity: FatigueGranularitySchema.optional(),
  topicDetailContext: TopicDetailContextQuerySchema.optional(),
  topicDetailKpi: z.string().optional(),
  /** When true, Fatigue may emit long-format `series` points. Absent = aggregated (old web). */
  topicDetailSplitSeries: z.boolean().optional(),
  compTopicDetailContext: CompTopicDetailContextQuerySchema.optional(),
  // Examples tabs paginate against BigQuery directly (LIMIT/OFFSET), so each
  // batch carries its own offset. Absent/0 for the first batch and fatigue.
  topicDetailOffset: z.number().int().min(0).optional(),
}).superRefine((payload, ctx) => {
  if (payload.page === 'overview-topic-detail-examples') {
    if (!payload.topicDetailContext) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'topicDetailContext is required for overview-topic-detail-examples',
        path: ['topicDetailContext'],
      });
    }
    if (payload.filtersPayload.mode !== 'my-brand') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'overview-topic-detail-examples requires my-brand filters',
        path: ['filtersPayload', 'mode'],
      });
    }
  }
  if (payload.page === 'overview-topic-detail-fatigue') {
    if (!payload.topicDetailContext) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'topicDetailContext is required for overview-topic-detail-fatigue',
        path: ['topicDetailContext'],
      });
    }
    if (!payload.topicDetailKpi) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'topicDetailKpi is required for overview-topic-detail-fatigue',
        path: ['topicDetailKpi'],
      });
    }
    if (payload.filtersPayload.mode !== 'my-brand') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'overview-topic-detail-fatigue requires my-brand filters',
        path: ['filtersPayload', 'mode'],
      });
    }
  }
  if (payload.page === 'overview-comp-topic-detail-examples') {
    if (!payload.compTopicDetailContext) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'compTopicDetailContext is required for overview-comp-topic-detail-examples',
        path: ['compTopicDetailContext'],
      });
    }
    if (payload.filtersPayload.mode !== 'competitors') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'overview-comp-topic-detail-examples requires competitors filters',
        path: ['filtersPayload', 'mode'],
      });
    }
  }
  if (payload.page === 'overview-comp-topic-detail-fatigue') {
    if (!payload.compTopicDetailContext) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'compTopicDetailContext is required for overview-comp-topic-detail-fatigue',
        path: ['compTopicDetailContext'],
      });
    }
    if (payload.filtersPayload.mode !== 'competitors') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'overview-comp-topic-detail-fatigue requires competitors filters',
        path: ['filtersPayload', 'mode'],
      });
    }
  }
});

export type WorkspaceDataRefreshRequestedPayload = z.infer<typeof WorkspaceDataRefreshRequestedPayload>;

// ─── WORKSPACE.OPTIONS_REQUESTED payload ──────────────────────────────
// Filter-option lookups and single-entity drill-ins the API used to run against
// BigQuery directly. The API resolves the tenant's BQ client (Postgres read) and passes
// it here so the worker owns BigQuery; the worker caches the result under the
// byte-identical key the API's fast-path cache-hit check reads. Discriminated on `kind`
// so each option query carries only the scalars it needs. The group-by cache key
// is the resolved window (scalars — no object serialization). `dateRange` stays
// optional so an older API pod that still publishes the token keeps parsing;
// the worker falls back through `windowParams` the same way the BFF does.
// Ceiling for the legacy `daysBack` field old API pods still publish. New
// publishers send the resolved window; both shapes must parse during a mixed deploy.

export const WorkspaceOptionsRequestedPayload = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('overview-group-by'),
    tenantId: z.string().cuid2(),
    clientKey: z.string(),
    groupBy: z.string(),
    startDate: z.string().default(''),
    endDate: z.string().default(''),
    dateRange: DateRange.optional(),
  }),
  // `clientKey` builds the SQL, but the result cache is keyed on `tenantId` — see the
  // key builders in the worker's cache module.
  z.object({
    kind: z.literal('my-brand-filters'),
    tenantId: z.string().cuid2(),
    clientKey: z.string(),
  }),
  z.object({
    kind: z.literal('competitor-options'),
    tenantId: z.string().cuid2(),
    industry: z.string().min(1),
    source: z.string().min(1).default('All'),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  }),
  z.object({
    kind: z.literal('competitor-platform-options'),
    tenantId: z.string().cuid2(),
    industry: z.string().min(1),
  }),
  z.object({
    kind: z.literal('industry-products'),
    tenantId: z.string().cuid2(),
    industry: z.string().min(1),
  }),
  z.object({
    kind: z.literal('drilldown-creative-hash'),
    tenantId: z.string().cuid2(),
    hash: z.string().min(1),
    industry: z.string().min(1),
    // Expand/contract: new publishers send the resolved window; old API pods still
    // send only `daysBack`. Both shapes must parse during a mixed deploy.
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    dateRange: DateRange.optional(),
    daysBack: z.number().int().min(1).max(MAX_DAYS_BACK).optional(),
  }),
]).superRefine((val, ctx) => {
  if (val.kind === 'competitor-options') {
    const startDate = val.startDate?.trim() ?? '';
    const endDate = val.endDate?.trim() ?? '';
    if (startDate === '' && endDate === '') return;
    if (!isAcceptableDateWindow(startDate, endDate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'startDate and endDate must both be absent or a real ordered YYYY-MM-DD window within the span cap',
      });
    }
    return;
  }
  if (val.kind !== 'drilldown-creative-hash') return;
  const startDate = val.startDate ?? '';
  const endDate = val.endDate ?? '';
  if (startDate !== '' || endDate !== '') {
    if (!isAcceptableDateWindow(startDate, endDate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'startDate and endDate must be a real ordered YYYY-MM-DD window within the span cap',
      });
    }
    return;
  }
  if (val.daysBack !== undefined || val.dateRange !== undefined) return;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: 'drilldown-creative-hash requires a date window, daysBack, or dateRange',
  });
});

export type WorkspaceOptionsRequestedPayload = z.infer<typeof WorkspaceOptionsRequestedPayload>;

// ─── Option row shapes shared by the API gateways and the worker ───────
// Declared here so both sides agree on what lands in the result cache. The frontend
// keeps its own equivalents next to its fetchers — relocating those would churn imports
// across the opportunities feature for no functional gain.

/** One selectable competitor brand for the industry-scoped brand pickers. */
export const CompetitorOptionSchema = z.object({
  name: z.string(),
  imageUrl: z.string().nullable(),
  /** Null when BigQuery returns a non-numeric count for the brand. */
  assetCount90d: z.number().nullable(),
});

/** One selectable product within an industry. `isFocused` marks the tenant's own. */
export const IndustryProductOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  isFocused: z.boolean(),
});

export const CompetitorPlatformOptionsResponseSchema = z.object({
  platforms: z.array(z.string()),
});

export type CompetitorOption = z.infer<typeof CompetitorOptionSchema>;
export type IndustryProductOption = z.infer<typeof IndustryProductOptionSchema>;
export type CompetitorPlatformOptionsResponse = z.infer<
  typeof CompetitorPlatformOptionsResponseSchema
>;
